import React, { Suspense, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { Provider, useSelector } from 'react-redux'
import store from './store'
import { Routes as AppRoutes } from './utils/routes'
import Login from './pages/Login'
import Account from './pages/Account'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import 'react-toastify/dist/ReactToastify.css'
import './index.generated.scss'
import { setupInterceptors } from './utils/axios'
import Loader from './components/Loader'
import Promotions from './pages/Promotions'
import HomePage from './pages/HomePage'
import CasinoPage from './pages/CasinoPage'
import LoyaltyBonus from './pages/LoyaltyBonus'
// import CashbackBonus from './pages/CashbackBonus'
// import { getItem } from './utils/storageUtils'
import { ScrollToTop } from './utils/helpers'
import BlockedCountry from './pages/BlockedCountry'
// import Tournaments from './pages/Tournament'
// import TemporaryPause from './components/TemporaryPause'
// import TournamentIndex from './pages/Tournament/components/index'
import NewSignup from './pages/NewSignup'
// import Signup from './pages/Signup'
import PrivateRoute from './components/PrivateRoute'
import PreAppLoader from './components/PreAppLoader'
import MetaTags from './components/MetaTags/MetaTags'
import RouteGuard from './components/RouteGuard'
import ZohoChatButtonControl from 'components/ZohoChatButtonControl'
import AntetrekPopupWidget from 'components/AntetrekPopupWidget'
import FallbackRoute from './components/FallbackRoute'
import AffiliateTokenHandler from './components/AffiliateTokenHandler'
import ModalsHandler from './components/Modal'
import AuthModalUrlHandler from './components/AuthModal/UrlHandler'
import { ToastMessageContainer } from './components/Toast'

// const currentURL = window.location.host
// if (currentURL) {
//   scssConstants(currentURL)
// }

const Layout = React.lazy(() => import('./components/Layout'))
const PlayGame = React.lazy(() => import('./pages/PlayGame'))
const MobileGameLayout = React.lazy(() => import('./components/MobileGameLayout'))
const DemoGame = React.lazy(() => import('./pages/PlayGame/demo'))
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'))
const NotFound = React.lazy(() => import('./pages/NotFound'))
const Forbidden = React.lazy(() => import('./pages/Forbidden'))
const SiteMaintenance = React.lazy(() => import('./pages/SiteMaintenance'))
const SiteMaintenanceStatic = React.lazy(() => import('./pages/SiteMaintenanceStatic'))
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'))
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const ResetEmail = React.lazy(() => import('./pages/ResetEmail'))
// const TenantInactive = React.lazy(() => import('./pages/TenantInactive'))
const PromotionsPage = React.lazy(() => import('./pages/Promotions/components/PromotionsPage'))
const Cms = React.lazy(() => import('./pages/Cms'))
const ResendEmail = React.lazy(() => import('./pages/ResendEmail'))
const SuccessReceipt = React.lazy(() => import('./components/Receipts/SuccessReceipt'))
const CookieModal = React.lazy(() => import('./components/CookieModal'))
const VIPPage = React.lazy(() => import('./pages/VIP'))
const Affiliate = React.lazy(() => import('./pages/Affiliate'))
const WordPressAdmin = React.lazy(() => import('./pages/WordPressAdmin'))
const Calendar = React.lazy(() => import('./pages/Calendar'))
const SportsbookPage = React.lazy(() => import('./pages/SportsbookPage'))

const RouterContent = () => {
  const modalBasedAuth = useSelector(state => state?.uiConfig?.general?.modalBasedAuth)
  const location = useLocation()
  const isInitialPageView = useRef(true)

  // CIO auto page tracking is disabled to keep the affiliate referrer out of
  // CIO (AMGP-3573); page views are fired manually instead. The landing page
  // view is sent by cioEnableScript — this effect covers SPA route changes.
  //
  // Customer.io's new JS source does not auto-track SPA navigations, so page-view
  // based journeys, segments and in-app page rules need an explicit page() call on
  // every route change. The initial page view is already tracked by cioEnableScript.
  useEffect(() => {
    if (isInitialPageView.current) {
      isInitialPageView.current = false
      return
    }
    if (process.env.REACT_APP_CIO_NEW_SOURCE !== 'true' || !window.cioanalytics) {
      window.cioTrackPageView?.()
    } else {
      window.cioanalytics.page?.({ referrer: '' })
    }
  }, [location.pathname])

  return (
    <Suspense fallback={<Loader top='40vh' />}>
      <Routes>
        <Route path={AppRoutes.login} element={<RouteGuard tab='login' element={<Login />} />} />
        <Route path={AppRoutes.signup} element={<RouteGuard tab='signup' identifier='signup' fallback={AppRoutes.login}><NewSignup /></RouteGuard>} />
        <Route path={AppRoutes.forgotPassword} element={<RouteGuard tab='forgot-password' element={<ForgotPassword />} />} />
        {/* <Route path={AppRoutes.tenantInactive} element={<TenantInactive />} /> */}
        <Route
          path={AppRoutes.notFound}
          element={
            <Layout>
              <NotFound />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.calendar}
          element={
            <Layout>
              <Calendar />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.wpAdmin}
          element={
            <WordPressAdmin />
          }
        />
        <Route
          path={AppRoutes.forbidden}
          element={
            <Forbidden />
          }
        />
        <Route
          path={AppRoutes.blockedCountry}
          element={
            <BlockedCountry />
          }
        />
        <Route
          path={AppRoutes.siteMaintenance}
          element={
            <SiteMaintenance />
          }
        />
        <Route
          path={AppRoutes.siteMaintenanceStatic}
          element={
            <SiteMaintenanceStatic />
          }
        />
        <Route path={AppRoutes.resetPassword} element={<ResetPassword />} />
        {!modalBasedAuth && (<Route path={AppRoutes.resendEmail} element={<ResendEmail />} />)}
        <Route
          path={AppRoutes.homepage}
          element={
            <Layout>
              <HomePage />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.casinoSubCategory}
          element={
            <Layout>
              <CasinoPage />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.promotionpage}
          element={
            <RouteGuard identifier='promotionpage'>
              <Layout>
                <Promotions />
              </Layout>
            </RouteGuard>
          }
        />
        {/* <Route
          path={AppRoutes.CashbackBonus}
          element={
            <Layout>
              <CashbackBonus />
            </Layout>
          }
        /> */}
        <Route
          path={AppRoutes.loyaltyBonusPage}
          element={
            <RouteGuard identifier='loyaltyBonusPage'>
              <Layout>
                <LoyaltyBonus />
              </Layout>
            </RouteGuard>
          }
        />
        <Route
          path={AppRoutes.sportsbook}
          element={
            <RouteGuard identifier='sportsbook'>
              <Layout>
                <SportsbookPage />
              </Layout>
            </RouteGuard>
          }
        />
        <Route
          path={AppRoutes.promotionpageId}
          element={
            <RouteGuard identifier='promotionpage'>
              <Layout>
                <PromotionsPage />
              </Layout>
            </RouteGuard>
          }
        />
        <Route
          path={AppRoutes.cms}
          element={
            <Layout>
              <Cms />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.account}
          element={
            <Layout isPrivateRoute>
              <Account />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.playGame}
          element={
            <Layout isPrivateRoute>
              <PlayGame />
            </Layout>
          }
        />
        {/* New Route added for only mobile devices to maintain separate UI */}
        <Route
          path={AppRoutes.mobilePlayGame}
          element={
            <PrivateRoute>
              <MobileGameLayout />
            </PrivateRoute>
          }
        />
        <Route
          path={AppRoutes.demoGame}
          element={
            <Layout>
              <DemoGame />
            </Layout>
          }
        />
        <Route
          path={AppRoutes.emailTokenLink}
          element={
            <VerifyEmail />
          }
        />

        <Route
          path={AppRoutes.resetEmail}
          element={
            <ResetEmail />
          }
        />

        <Route
          path={AppRoutes.paymentStatus}
          element={
            <Layout isPrivateRoute>
              <SuccessReceipt />
            </Layout>
          }
        />

        <Route
          path={AppRoutes.paymentSuccess}
          element={
            <Layout isPrivateRoute>
              <Account />
            </Layout>
          }
        />

        <Route
          path={AppRoutes.userpromotionpageId}
          element={
            <Layout isPrivateRoute>
              <PromotionsPage />
            </Layout>
          }
        />

        {/* <Route
        path='*'
        element={
          <Navigate
            to={pathName?.includes(window?.location?.pathname) && `/${getItem('language')?.toLowerCase() || 'en'}${window?.location?.pathname}`}
            replace
          />
        }
      /> */}

        {/* <Route
            path={AppRoutes.tournaments} element={
              <Layout>
                <Tournaments />
              </Layout>
            }
          /> */}

        {/* <Route
        path={AppRoutes.tournamentDetails} element={
          <Layout>
            <TournamentIndex />
          </Layout>
        }
      /> */}

        <Route
          path={AppRoutes.VIP} element={
            <RouteGuard identifier='VIP'>
              <Layout>
                <VIPPage />
              </Layout>
            </RouteGuard>
          }
        />

        <Route
          path={AppRoutes.affiliate} element={
            <RouteGuard identifier='affiliate'>
              <Layout>
                <Affiliate />
              </Layout>
            </RouteGuard>
          }
        />

        {/* FIXME: Duplicate entry for homepage route */}
        <Route
          path={AppRoutes.home}
          element={
            <Layout>
              <HomePage />
            </Layout>
          }
        />

        {/* Fallback route for any invalid URLs */}
        <Route path='*' element={<FallbackRoute />} />

      </Routes>
    </Suspense>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root'))

// const pathName = Object.keys(AppRoutes).flatMap((path) => AppRoutes[path].replace('/:languageCode/', '/'))

setupInterceptors()

if (process.env.REACT_APP_ON_MAINTENANCE === 'true') {
  root.render(
    <Provider store={store}>
      <Suspense fallback={<Loader top='40vh' />}>
        <SiteMaintenanceStatic />
      </Suspense>
    </Provider>)
} else {
  root.render(
    <Provider store={store}>
      <PreAppLoader>
        <ToastMessageContainer />
        <Router>
          <ScrollToTop />
          <CookieModal />
          <AffiliateTokenHandler />
          <MetaTags />
          <ZohoChatButtonControl />
          <AntetrekPopupWidget />
          <ModalsHandler />
          <AuthModalUrlHandler />
          <RouterContent />
        </Router>
      </PreAppLoader>
    </Provider>)
}
